/**
 * Copyright (c) 2022 - Present. Blend Labs, Inc. All rights reserved
 * Blend Confidential - Restricted
 */
import { useMemo } from 'react';
import { useRootSelector } from '../../../../../hooks/useRootSelector';
import { SigningActionType } from '@shared/constants/signing';
import { DocumentStatus } from '@shared/constants/document';

export enum MobileDocSubheaderTextScenarios {
  documentBeingSigned,
  documentBeingNotarized,
  documentBeingReviewed,
  documentCompleted,
  documentVoided,
}

export const getMobileDocSubheaderTextMap = {
  [MobileDocSubheaderTextScenarios.documentBeingSigned]: (signingActionSignerName?: string) =>
    `${signingActionSignerName} is signing this document`,
  [MobileDocSubheaderTextScenarios.documentBeingNotarized]: (signingActionSignerName?: string) =>
    `${signingActionSignerName} is notarizing this document`,
  [MobileDocSubheaderTextScenarios.documentBeingReviewed]: (signingActionSignerName?: string) =>
    `${signingActionSignerName} is reviewing this document`,
  [MobileDocSubheaderTextScenarios.documentCompleted]: 'This document has been completed',
  [MobileDocSubheaderTextScenarios.documentVoided]: 'This document has been voided',
};

export const useMobileDocSubheaderText = () => {
  const {
    selectedDocumentStatus,
    selectedDocumentId,
    signingActionType,
    signingActionDocumentId,
    signingActionSignerName,
    isNotary,
  } = useRootSelector(({ document, signingRoom }) => {
    const selectedDocId = document?.selectedDoc?.id;
    const selectedDocStatus = document?.documents?.find(doc => doc.id === selectedDocId)?.status;
    const signingAction = signingRoom?.signingAction;

    return {
      selectedDocumentStatus: selectedDocStatus,
      selectedDocumentId: selectedDocId,
      signingActionType: signingAction?.type,
      signingActionDocumentId: signingAction?.document?.id,
      signingActionSignerName: signingAction?.signerName,
      isNotary: signingAction?.isNotary,
    };
  });

  return useMemo(() => {
    if (signingActionType === SigningActionType.SIGN) {
      return getMobileDocSubheaderTextMap[MobileDocSubheaderTextScenarios.documentBeingSigned](signingActionSignerName);
    }
    if (selectedDocumentStatus === DocumentStatus.PENDING_REVIEW) {
      return getMobileDocSubheaderTextMap[MobileDocSubheaderTextScenarios.documentBeingReviewed](
        signingActionSignerName,
      );
    }
    if (signingActionType === SigningActionType.VIEW && selectedDocumentId === signingActionDocumentId && isNotary) {
      return getMobileDocSubheaderTextMap[MobileDocSubheaderTextScenarios.documentBeingNotarized](
        signingActionSignerName,
      );
    }
    if (signingActionType === SigningActionType.VIEW && selectedDocumentId === signingActionDocumentId) {
      return getMobileDocSubheaderTextMap[MobileDocSubheaderTextScenarios.documentBeingSigned](signingActionSignerName);
    }
    if (selectedDocumentStatus === DocumentStatus.COMPLETED) {
      return getMobileDocSubheaderTextMap[MobileDocSubheaderTextScenarios.documentCompleted];
    }
    if (selectedDocumentStatus === DocumentStatus.VOIDED) {
      return getMobileDocSubheaderTextMap[MobileDocSubheaderTextScenarios.documentVoided];
    }

    return null;
  }, [signingActionType, signingActionSignerName, selectedDocumentId, selectedDocumentStatus, signingActionDocumentId]);
};
